import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [pharmacy, setPharmacy] = useState(null);
  const [net, setNet] = useState({ ip: null, ip_allowed: true });
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((data) => {
    setUser(data.user);
    setPharmacy(data.pharmacy);
    if (data.ip) setNet({ ip: data.ip, ip_allowed: data.ip_allowed, emergency: data.emergency_active });
  }, []);

  useEffect(() => {
    localStorage.removeItem("token");
    api
      .get("/auth/me")
      .then(({ data }) => applySession(data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, [applySession]);

  const finishLogin = useCallback(
    (data) => {
      applySession(data);
      return data.user;
    },
    [applySession]
  );

  const loginOwner = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      return finishLogin(data);
    },
    [finishLogin]
  );

  const registerPharmacy = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("phCode", data.pharmacy.code);
      return finishLogin(data);
    },
    [finishLogin]
  );

  const loginPin = useCallback(
    async (code, pin, emergency_token) => {
      const { data } = await api.post("/auth/pin-login", { code, pin, emergency_token: emergency_token || null });
      localStorage.setItem("phCode", code);
      return finishLogin(data);
    },
    [finishLogin]
  );

  const refresh = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    applySession(data);
    return data;
  }, [applySession]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    localStorage.removeItem("token");
    setUser(null);
    setPharmacy(null);
  }, []);

  const value = useMemo(
    () => ({ user, pharmacy, setPharmacy, net, loading, loginOwner, loginPin, registerPharmacy, logout, refresh }),
    [user, pharmacy, net, loading, loginOwner, loginPin, registerPharmacy, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
